import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { api } from "helpers/axios";
import { empty, errorMessage, scrollInto } from "helpers/common";
import { toast } from "helpers/toast";
import ErrorAlert from "components/ui/ErrorAlert";
import { Button, Input, Modal, NativeSelect } from "components/ui";
import * as Sentry from "@sentry/react";
import {
  fetchPayfactors,
  fetchGlobalGradeSelections,
  fetchGlobalGroupSelections,
  fetchGlobalPayfactorMappings,
  fetchGlobalPayfactorValues,
  getPayfactors,
  selectGlobalGradeSelections,
  selectGlobalGroupSelections,
  selectGlobalPayfactorMappingGroupMessages,
  selectGlobalPayfactorMappings,
  selectGlobalPayfactorValues,
  updateGlobalGradeSelections,
  updateGlobalGroupSelections,
} from "store/payfactorSlice";
import { useTranslation } from "translation/useTranslation";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import TableScrollbar from "components/EmployeeManagement/TableScrollbar";
import { isDbModule, normalizeModule } from "helpers/module";
import { getPermissions } from "store/userSlice";
import hasPermissionTo from "helpers/permission";

const KEY_SEPARATOR = "::";
const moduleOrder = (value) => normalizeModule(value, null) === 'db' ? 2 : 1;

const buildGroupKey = (moduleKey, code) => `${moduleKey ?? ""}${KEY_SEPARATOR}${code ?? ""}`;
const buildMappingKey = (grade) => `${grade ?? ""}`;
const buildGradeSelectionKey = (moduleKey, groupCode, grade) => `${moduleKey ?? ""}${KEY_SEPARATOR}${groupCode ?? ""}${KEY_SEPARATOR}${grade ?? ""}`;

const normalizePrefix = (value) => `${value ?? ""}`.trim();

const extractGradeSuffix = (grade, groupCode) => {
  const normalizedGrade = `${grade ?? ""}`;
  const normalizedGroupCode = `${groupCode ?? ""}`;

  if (!normalizedGrade) return null;
  if (!normalizedGroupCode) return normalizedGrade;
  if (normalizedGrade.startsWith(normalizedGroupCode)) {
    return normalizedGrade.slice(normalizedGroupCode.length);
  }

  return null;
};

const buildExpectedExternalLabel = (grade, groupCode, prefix) => {
  const normalizedPrefix = normalizePrefix(prefix);
  if (!normalizedPrefix) return "";

  const suffix = extractGradeSuffix(grade, groupCode);
  if (suffix === null) {
    return `${normalizedPrefix}${grade ?? ""}`;
  }

  return `${normalizedPrefix}${suffix}`;
};

const parseViewerNumeric = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const raw = `${value}`.trim();
  if (!raw) return null;

  const normalized = raw.includes(",") && !raw.includes(".")
    ? raw.replace(",", ".")
    : raw;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatViewerValue = (value) => {
  const parsed = parseViewerNumeric(value);
  if (parsed === null) return `${value ?? "-"}`;

  return new Intl.NumberFormat("de-DE", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  }).format(parsed);
};

// Thunk rejections and our own Errors already carry a readable message; Axios errors need errorMessage for the server message.
const toErrorState = (err, context) => (typeof err === "object" && err?.message && !err.isAxiosError ? err : errorMessage(err, context));

const formatViewerCount = (value) => {
  return new Intl.NumberFormat("de-DE").format(Number(value || 0));
};

function PayfactorValuesMatrixTable({ section, columnCount, hasBaseColumn, matrixTableTheadRefs, index }) {
  const { editableTranslate } = useTranslation();

  const tableRef = useRef(null);

  const [resizedCount, setResizedCount] = useState(0);

  useEffect(() => {
    setResizedCount(c => c + 1);
  }, [section]);

  return <>
    <div className="table-responsive has-custom-scrollbar">
      <table ref={tableRef} className="table common-table mb-0 payfactor-values-matrix-table" style={{ minWidth: `${Math.max(520, 150 + ((columnCount - 1) * 125))}px` }} data-testid="grades-table">
        <thead className="sticky-top" ref={el => (matrixTableTheadRefs.current[index] = el)}>
          <tr>
            <th>{editableTranslate("payfactor_settings.internal_value", "Interner Wert")}</th>
            {section.levels.map((level) => (
              <th key={`${section.group_key}-level-${level.key}`} className="text-right">
                {level.label}
              </th>
            ))}
            {hasBaseColumn && (
              <th className="text-right">
                {editableTranslate("payfactor_settings.value_label", "Wert")}
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {section.grades.map((grade) => (
            <tr key={`${section.group_key}-${grade.grade}`} data-testid={`row-${grade?.grade}`}>
              <td className="payfactor-values-grade-cell sticky-col">
                <strong>{grade.grade_label || grade.grade || "-"}</strong>
              </td>
              {section.levels.map((level) => (
                <td key={`${section.group_key}-${grade.grade}-${level.key}`} className="payfactor-values-number">
                  {grade.valuesByLevel[level.key] === undefined ? (
                    <span className="text-muted">-</span>
                  ) : formatViewerValue(grade.valuesByLevel[level.key])}
                </td>
              ))}
              {hasBaseColumn && (
                <td className="payfactor-values-number">
                  {grade.unlevelledValue === null ? (
                    <span className="text-muted">-</span>
                  ) : formatViewerValue(grade.unlevelledValue)}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <TableScrollbar tableRef={tableRef} data={section.grades} trigger={resizedCount} className="z-3" />
  </>;
}

export default function PayFactors() {
  const dispatch = useDispatch();
  const { translate, editableTranslate, editableTranslateString } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showSyncConfirmation, setShowSyncConfirmation] = useState(false);
  const [syncSummary, setSyncSummary] = useState(null);
  const [error, setError] = useState(null);

  const [groupDrafts, setGroupDrafts] = useState({});
  const [initialGroupDrafts, setInitialGroupDrafts] = useState({});

  const [mappingDrafts, setMappingDrafts] = useState({});
  const [initialMappingDrafts, setInitialMappingDrafts] = useState({});

  const [gradeSelectionDrafts, setGradeSelectionDrafts] = useState({});
  const [initialGradeSelectionDrafts, setInitialGradeSelectionDrafts] = useState({});

  const [masterPrefixDrafts, setMasterPrefixDrafts] = useState({});
  const [initialMasterPrefixDrafts, setInitialMasterPrefixDrafts] = useState({});
  const [manualOverrideByGrade, setManualOverrideByGrade] = useState({});
  const [initialManualOverrideByGrade, setInitialManualOverrideByGrade] = useState({});

  const [expandedGroups, setExpandedGroups] = useState({});
  const [activeModuleKey, setActiveModuleKey] = useState(null);
  const [selectedViewerPayfactorHashId, setSelectedViewerPayfactorHashId] = useState(null);
  const [viewerValuesLoading, setViewerValuesLoading] = useState(false);
  const [viewerValuesError, setViewerValuesError] = useState(null);
  const [viewerValues, setViewerValues] = useState([]);
  const [viewerSearchTerm, setViewerSearchTerm] = useState("");
  const [viewerGroupFilter, setViewerGroupFilter] = useState("all");
  const [viewerLevelFilter, setViewerLevelFilter] = useState("all");

  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      setLoading(true);
      setError(null);

      try {
        await Promise.all([
          dispatch(fetchPayfactors()).unwrap(),
          dispatch(fetchGlobalPayfactorValues({ force: true })).unwrap(),
          dispatch(fetchGlobalGroupSelections()).unwrap(),
          dispatch(fetchGlobalPayfactorMappings()).unwrap(),
          dispatch(fetchGlobalGradeSelections()).unwrap(),
        ]);
      } catch (err) {
        if (isMounted) {
          setError(toErrorState(err, "load pay factor settings"));
          scrollInto(".content-card");
        }
        Sentry.captureException(err);
      }

      if (isMounted) setLoading(false);
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [dispatch]);

  const currentPayfactorValues = useSelector(selectGlobalPayfactorValues);
  const currentMappings = useSelector(selectGlobalPayfactorMappings);
  const storedPayfactors = useSelector(getPayfactors);
  const payfactors = useMemo(() => storedPayfactors || [], [storedPayfactors]);
  const permissions = useSelector(getPermissions) || [];
  const canSyncPayfactors = hasPermissionTo("settings.pay_factors.*", permissions);
  const mappingGroupMessages = useSelector(selectGlobalPayfactorMappingGroupMessages);
  const currentGroupSelections = useSelector(selectGlobalGroupSelections);
  const currentGradeSelections = useSelector(selectGlobalGradeSelections);
  const resolveModuleLabel = (moduleLabel, moduleKey) => {
    const fallback = `${moduleLabel ?? moduleKey ?? ""}`.trim();
    if (!moduleLabel) return fallback || `${moduleKey ?? ""}`;

    return translate(`global.module.${moduleLabel}`, fallback || `${moduleKey ?? ""}`);
  };

  const matrixHeaderRefs = useRef([]);
  const matrixTableTheadRefs = useRef([]);

  const mappingMessagesByGroupKey = useMemo(() => {
    return mappingGroupMessages.reduce((messages, item) => {
      const key = buildGroupKey(item.module, item.group_code);
      if (!key || !item?.message || messages[key]) return messages;
      messages[key] = item.message;
      return messages;
    }, {});
  }, [mappingGroupMessages]);

  const groupsWithGrades = useMemo(() => {
    const groupGradesMap = currentPayfactorValues.reduce((accumulator, row) => {
      if (!row?.grade) return accumulator;

      const groupKey = buildGroupKey(row.module, row.group_code);
      if (!accumulator[groupKey]) {
        accumulator[groupKey] = {
          module: row.module,
          module_key: row.module_key || normalizeModule(row.module, null),
          group_code: row.group_code,
          gradesMap: new Map(),
        };
      }

      if (!accumulator[groupKey].gradesMap.has(row.grade)) {
        accumulator[groupKey].gradesMap.set(row.grade, {
          grade: row.grade,
          grade_label: row.grade_label,
        });
      }

      return accumulator;
    }, {});

    return currentGroupSelections
      .map((group) => {
        const groupKey = buildGroupKey(group.module, group.code);
        const gradeSource = groupGradesMap[groupKey];

        return {
          ...group,
          group_key: groupKey,
          grades: gradeSource
            ? Array.from(gradeSource.gradesMap.values()).sort((left, right) => `${left.grade}`.localeCompare(`${right.grade}`))
            : [],
        };
      })
      .sort((left, right) => {
        if (left.module === right.module) {
          return `${left.code}`.localeCompare(`${right.code}`);
        }

        return moduleOrder(left.module) - moduleOrder(right.module);
      });
  }, [currentGroupSelections, currentPayfactorValues]);

  const moduleSections = useMemo(() => {
    const grouped = new Map();

    groupsWithGrades.forEach((group) => {
      const key = `${group.module}`;
      const section = grouped.get(key) || {
        module: group.module,
        module_key: group.module_key || normalizeModule(group.module, null),
        groups: [],
      };
      section.groups.push(group);
      grouped.set(key, section);
    });

    return Array.from(grouped.values()).sort((left, right) => moduleOrder(left.module) - moduleOrder(right.module));
  }, [groupsWithGrades]);

  const hasModuleTabs = moduleSections.length > 1;

  useEffect(() => {
    if (moduleSections.length === 0) {
      setActiveModuleKey(null);
      return;
    }

    if (!moduleSections.some((section) => normalizeModule(section.module, null) === normalizeModule(activeModuleKey, null))) {
      setActiveModuleKey(moduleSections[0].module);
    }
  }, [moduleSections, activeModuleKey]);

  const activeModuleSection = useMemo(() => {
    return moduleSections.find((section) => normalizeModule(section.module, null) === normalizeModule(activeModuleKey, null)) || null;
  }, [moduleSections, activeModuleKey]);
  const isStateModuleActive = isDbModule(activeModuleSection?.module_key || activeModuleSection?.module);

  const visibleGroups = useMemo(() => {
    if (!hasModuleTabs) return groupsWithGrades;

    return activeModuleSection?.groups || [];
  }, [hasModuleTabs, groupsWithGrades, activeModuleSection]);

  useEffect(() => {
    if (!Array.isArray(payfactors) || payfactors.length === 0) {
      setSelectedViewerPayfactorHashId(null);
      return;
    }

    if (selectedViewerPayfactorHashId && payfactors.some((item) => item.hash_id === selectedViewerPayfactorHashId)) {
      return;
    }

    const preferred = payfactors.find((item) => !!item.is_active) || payfactors[0];
    setSelectedViewerPayfactorHashId(preferred?.hash_id || null);
  }, [payfactors, selectedViewerPayfactorHashId]);

  useEffect(() => {
    let isMounted = true;

    const loadViewerValues = async () => {
      if (!selectedViewerPayfactorHashId || !activeModuleSection || isStateModuleActive) {
        if (isMounted) {
          setViewerValues([]);
          setViewerValuesError(null);
          setViewerValuesLoading(false);
        }
        return;
      }

      setViewerValuesLoading(true);
      setViewerValuesError(null);
      setViewerValues([]);

      try {
        const params = {
          apply_filters: false,
        };

        if (activeModuleKey !== null && activeModuleKey !== undefined) {
          params.module = activeModuleKey;
        }

        const { data, status } = await api.get(`/pay-factors/${selectedViewerPayfactorHashId}/values`, { params });

        if (!isMounted) return;

        if (status === 200) {
          setViewerValues(Array.isArray(data?.data) ? data.data : []);
          return;
        }

        setViewerValues([]);
      } catch (err) {
        if (!isMounted) return;

        setViewerValues([]);
        setViewerValuesError(errorMessage(err, "load pay factor table values"));
      } finally {
        if (isMounted) setViewerValuesLoading(false);
      }
    };

    loadViewerValues();

    return () => {
      isMounted = false;
    };
  }, [selectedViewerPayfactorHashId, activeModuleKey, activeModuleSection, isStateModuleActive]);

  useEffect(() => {
    setViewerGroupFilter("all");
    setViewerLevelFilter("all");
  }, [selectedViewerPayfactorHashId, activeModuleKey]);

  const sharedGroupCountByGrade = useMemo(() => {
    const gradeGroupMap = {};

    groupsWithGrades.forEach((group) => {
      group.grades.forEach((gradeRow) => {
        const key = buildMappingKey(gradeRow.grade);
        if (!gradeGroupMap[key]) {
          gradeGroupMap[key] = new Set();
        }
        gradeGroupMap[key].add(group.group_key);
      });
    });

    return Object.entries(gradeGroupMap).reduce((accumulator, [gradeKey, groupSet]) => {
      accumulator[gradeKey] = groupSet.size;
      return accumulator;
    }, {});
  }, [groupsWithGrades]);

  const selectedViewerPayfactor = useMemo(() => {
    return payfactors.find((item) => item.hash_id === selectedViewerPayfactorHashId) || null;
  }, [payfactors, selectedViewerPayfactorHashId]);

  const shouldShowValuesViewer = !!selectedViewerPayfactorHashId && !!activeModuleSection && !isStateModuleActive;

  const viewerGroupOptions = useMemo(() => {
    const grouped = new Map();

    viewerValues.forEach((row) => {
      const key = buildGroupKey(row.module, row.group_code);
      if (grouped.has(key)) return;
      grouped.set(key, {
        key,
        group_code: row.group_code,
        module: row.module,
        module_key: row.module_key || normalizeModule(row.module, null),
      });
    });

    return Array.from(grouped.values())
      .sort((left, right) => `${left.group_code ?? ""}`.localeCompare(`${right.group_code ?? ""}`));
  }, [viewerValues]);

  const filteredViewerValues = useMemo(() => {
    const normalizedSearch = viewerSearchTerm.trim().toLowerCase();

    return viewerValues.filter((row) => {
      if (viewerGroupFilter !== "all") {
        const rowGroupKey = buildGroupKey(row.module, row.group_code);
        if (rowGroupKey !== viewerGroupFilter) return false;
      }

      const hasLevel = !!(`${row.level ?? ""}`.trim());
      if (viewerLevelFilter === "with_level" && !hasLevel) return false;
      if (viewerLevelFilter === "without_level" && hasLevel) return false;

      if (!normalizedSearch) return true;
      const searchField = `${row.grade_label || row.grade || ""} ${row.level_label || row.level || ""} ${row.value ?? ""}`.toLowerCase();
      return searchField.includes(normalizedSearch);
    });
  }, [viewerValues, viewerSearchTerm, viewerGroupFilter, viewerLevelFilter]);

  const viewerMatrixSections = useMemo(() => {
    const compareLevel = (left, right) => {
      const leftRaw = `${left ?? ""}`.trim();
      const rightRaw = `${right ?? ""}`.trim();
      const leftNumber = Number(leftRaw);
      const rightNumber = Number(rightRaw);
      const hasLeftNumeric = leftRaw !== "" && Number.isFinite(leftNumber);
      const hasRightNumeric = rightRaw !== "" && Number.isFinite(rightNumber);

      if (hasLeftNumeric && hasRightNumeric) {
        return leftNumber - rightNumber;
      }

      return leftRaw.localeCompare(rightRaw);
    };

    const grouped = new Map();

    filteredViewerValues.forEach((row) => {
      const groupKey = buildGroupKey(row.module, row.group_code);
      const section = grouped.get(groupKey) || {
        group_key: groupKey,
        group_code: row.group_code,
        module_key: row.module_key || normalizeModule(row.module, null),
        module: row.module,
        levelsMap: new Map(),
        gradesMap: new Map(),
        has_unlevelled_values: false,
      };

      const gradeKey = `${row.grade ?? ""}`;
      const grade = section.gradesMap.get(gradeKey) || {
        grade: row.grade,
        grade_label: row.grade_label,
        valuesByLevel: {},
        unlevelledValue: null,
      };
      const level = `${row.level || row.level_label || ""}`.trim();
      const levelLabel = `${row.level_label || row.level || ""}`.trim();

      if (level) {
        section.levelsMap.set(level, levelLabel || level);
        grade.valuesByLevel[level] = row.value;
      } else {
        section.has_unlevelled_values = true;
        grade.unlevelledValue = row.value;
      }

      section.gradesMap.set(gradeKey, grade);
      grouped.set(groupKey, section);
    });

    return Array.from(grouped.values())
      .map((section) => ({
        ...section,
        levels: Array.from(section.levelsMap.entries())
          .sort(([left], [right]) => compareLevel(left, right))
          .map(([key, label]) => ({ key, label })),
        grades: Array.from(section.gradesMap.values()).sort((left, right) => `${left.grade ?? ""}`.localeCompare(`${right.grade ?? ""}`)),
      }))
      .sort((left, right) => `${left.group_code ?? ""}`.localeCompare(`${right.group_code ?? ""}`));
  }, [filteredViewerValues]);

  const handleExportViewerCsv = () => {
    if (filteredViewerValues.length === 0) return;

    const rows = [
      ["module", "group", "grade", "grade_label", "level", "level_label", "value"],
      ...filteredViewerValues.map((row) => [
        row.module_key || row.module || "",
        row.group_code || "",
        row.grade || "",
        row.grade_label || "",
        row.level || "",
        row.level_label || "",
        row.value ?? "",
      ]),
    ];

    const csv = rows
      .map((columns) => columns.map((cell) => `"${`${cell ?? ""}`.replace(/"/g, "\"\"")}"`).join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `payfactor-values-${selectedViewerPayfactor?.name || "table"}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const missingSelectedGroups = useMemo(() => {
    const fallbackMessages = groupsWithGrades
      .filter((group) => !!groupDrafts[group.group_key] && group.mapping_message)
      .map((group) => ({
        module: group.module,
        module_key: group.module_key || normalizeModule(group.module, null),
        group_code: group.code,
        message: group.mapping_message,
      }));

    const source = mappingGroupMessages.length > 0 ? mappingGroupMessages : fallbackMessages;
    const seen = new Set();

    return source.filter((item) => {
      const key = buildGroupKey(item.module, item.group_code);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [groupsWithGrades, groupDrafts, mappingGroupMessages]);

  const sharedGradeConflicts = useMemo(() => {
    return Object.values(sharedGroupCountByGrade).some((count) => count > 1);
  }, [sharedGroupCountByGrade]);

  useEffect(() => {
    const nextDrafts = {};
    currentGroupSelections.forEach((item) => {
      nextDrafts[buildGroupKey(item.module, item.code)] = !!item.is_enabled;
    });

    setGroupDrafts(nextDrafts);
    setInitialGroupDrafts(nextDrafts);
  }, [currentGroupSelections]);

  useEffect(() => {
    const nextDrafts = {};
    currentMappings.forEach((item) => {
      nextDrafts[buildMappingKey(item.grade)] = item.external_label || "";
    });

    setMappingDrafts(nextDrafts);
    setInitialMappingDrafts(nextDrafts);
  }, [currentMappings]);

  useEffect(() => {
    const nextDrafts = {};
    currentGradeSelections.forEach((item) => {
      nextDrafts[buildGradeSelectionKey(item.module, item.group_code, item.grade)] = item.is_enabled !== false;
    });

    setGradeSelectionDrafts(nextDrafts);
    setInitialGradeSelectionDrafts(nextDrafts);
  }, [currentGradeSelections]);

  useEffect(() => {
    const nextDrafts = {};
    const nextManualOverrides = {};

    groupsWithGrades.forEach((group) => {
      const persistedPrefix = normalizePrefix(group.external_label_prefix);
      if (persistedPrefix) {
        nextDrafts[group.group_key] = persistedPrefix;
      } else {
        nextDrafts[group.group_key] = `${group.code ?? ""}`;
      }

      group.grades.forEach((gradeRow) => {
        const gradeKey = buildMappingKey(gradeRow.grade);
        const currentLabel = `${initialMappingDrafts[gradeKey] ?? ""}`.trim();
        if (!currentLabel) return;

        const expectedLabel = buildExpectedExternalLabel(
          gradeRow.grade,
          group.code,
          nextDrafts[group.group_key],
        );

        if (currentLabel !== expectedLabel) {
          nextManualOverrides[gradeKey] = true;
        }
      });
    });

    setMasterPrefixDrafts(nextDrafts);
    setInitialMasterPrefixDrafts(nextDrafts);

    setManualOverrideByGrade(nextManualOverrides);
    setInitialManualOverrideByGrade(nextManualOverrides);
  }, [groupsWithGrades, initialMappingDrafts]);

  const editableGradeKeys = useMemo(() => {
    const keys = new Set();

    groupsWithGrades.forEach((group) => {
      group.grades.forEach((gradeRow) => {
        keys.add(buildMappingKey(gradeRow.grade));
      });
    });

    return [...keys];
  }, [groupsWithGrades]);

  const hasGroupChanges = useMemo(() => {
    const keys = new Set([...Object.keys(groupDrafts), ...Object.keys(initialGroupDrafts)]);
    for (const key of keys) {
      if ((groupDrafts[key] || false) !== (initialGroupDrafts[key] || false)) {
        return true;
      }
    }
    return false;
  }, [groupDrafts, initialGroupDrafts]);

  const hasPrefixChanges = useMemo(() => {
    const keys = new Set([...Object.keys(masterPrefixDrafts), ...Object.keys(initialMasterPrefixDrafts)]);
    for (const key of keys) {
      if (normalizePrefix(masterPrefixDrafts[key]) !== normalizePrefix(initialMasterPrefixDrafts[key])) {
        return true;
      }
    }

    return false;
  }, [masterPrefixDrafts, initialMasterPrefixDrafts]);

  const hasMappingChanges = useMemo(() => {
    const keys = new Set([...editableGradeKeys, ...Object.keys(initialMappingDrafts)]);

    for (const key of keys) {
      if ((mappingDrafts[key] || "").trim() !== (initialMappingDrafts[key] || "").trim()) {
        return true;
      }
    }

    return false;
  }, [mappingDrafts, initialMappingDrafts, editableGradeKeys]);

  const hasGradeSelectionChanges = useMemo(() => {
    const keys = new Set([
      ...Object.keys(gradeSelectionDrafts),
      ...Object.keys(initialGradeSelectionDrafts),
    ]);

    for (const key of keys) {
      const currentValue = gradeSelectionDrafts[key];
      const initialValue = initialGradeSelectionDrafts[key];

      if ((currentValue === undefined ? true : !!currentValue) !== (initialValue === undefined ? true : !!initialValue)) {
        return true;
      }
    }

    return false;
  }, [gradeSelectionDrafts, initialGradeSelectionDrafts]);

  useEffect(() => {
    const mHeaders = matrixHeaderRefs.current;
    if (mHeaders.length < 1) return;

    const mTheads = matrixTableTheadRefs.current;
    if (mTheads.length < 1) return;

    const handleScrollTable = () => {
      const navHeight = document.getElementById('navbar-menu')?.offsetHeight || 0;
      const toolbarHeight = document.getElementById('matrix-toolbar')?.offsetHeight || 0;
      const totalHeight = navHeight + toolbarHeight;

      let matchIndex = null;

      if (empty(viewerSearchTerm)) {
        for (let i = 0; i < mHeaders.length; i++) {
          const headerEl = mHeaders[i];
          const theadEl = mTheads[i];

          if (!headerEl || !theadEl) return;

          const headerParentCoordinates = headerEl.parentElement.getBoundingClientRect();
          const theadParentHeight = theadEl.parentElement.offsetHeight;

          if (headerParentCoordinates.y < totalHeight && headerParentCoordinates.y > (theadParentHeight - totalHeight) * -1) {
            headerEl.style.transform = `translate3d(0, ${-headerParentCoordinates.y + totalHeight}px, 0)`;
            theadEl.style.transform = `translate3d(0, ${-headerParentCoordinates.y + totalHeight}px, 0)`;
            matchIndex = i;
            break;
          }
        }
      }

      for (let i = 0; i < mHeaders.length; i++) {
        const headerEl = mHeaders[i];
        const theadEl = mTheads[i];

        if (!headerEl || !theadEl) return;

        if (matchIndex !== i) {
          headerEl.style.transform = 'translate3d(0,0,0)';
          theadEl.style.transform = 'translate3d(0,0,0)';
        }
      }
    };
    window.addEventListener('scroll', handleScrollTable);
    return () => window.removeEventListener('scroll', handleScrollTable);
  }, [viewerMatrixSections, viewerSearchTerm, matrixHeaderRefs, matrixTableTheadRefs]);

  const hasChanges = hasGroupChanges || hasPrefixChanges || hasMappingChanges || hasGradeSelectionChanges;

  const handleGroupToggle = (moduleKey, code, isEnabled) => {
    const groupKey = buildGroupKey(moduleKey, code);

    setGroupDrafts((prev) => ({
      ...prev,
      [groupKey]: isEnabled,
    }));

    setExpandedGroups((prev) => ({
      ...prev,
      [groupKey]: isEnabled,
    }));
  };

  const handleGradeSelectionToggle = (moduleKey, groupCode, grade, isEnabled) => {
    const key = buildGradeSelectionKey(moduleKey, groupCode, grade);

    setGradeSelectionDrafts((prev) => ({
      ...prev,
      [key]: isEnabled,
    }));
  };

  const handleSelectAllGrades = (group, isEnabled) => {
    setGradeSelectionDrafts((prev) => {
      const next = { ...prev };

      group.grades.forEach((gradeRow) => {
        next[buildGradeSelectionKey(group.module, group.code, gradeRow.grade)] = isEnabled;
      });

      return next;
    });
  };

  const handleMasterPrefixChange = (group, value) => {
    const groupKey = group.group_key;
    const nextPrefix = `${value ?? ""}`;

    setMasterPrefixDrafts((prev) => ({
      ...prev,
      [groupKey]: nextPrefix,
    }));

    setMappingDrafts((prev) => {
      const next = { ...prev };

      group.grades.forEach((gradeRow) => {
        const gradeKey = buildMappingKey(gradeRow.grade);
        if (manualOverrideByGrade[gradeKey]) return;

        next[gradeKey] = buildExpectedExternalLabel(gradeRow.grade, group.code, nextPrefix);
      });

      return next;
    });
  };

  const handleManualGradeMappingChange = (grade, value) => {
    const gradeKey = buildMappingKey(grade);

    setManualOverrideByGrade((prev) => ({
      ...prev,
      [gradeKey]: true,
    }));

    setMappingDrafts((prev) => ({
      ...prev,
      [gradeKey]: value,
    }));
  };

  const handleResetGradeToPrefix = (group, grade) => {
    const gradeKey = buildMappingKey(grade);
    const currentPrefix = masterPrefixDrafts[group.group_key] || "";

    setManualOverrideByGrade((prev) => {
      const next = { ...prev };
      delete next[gradeKey];
      return next;
    });

    setMappingDrafts((prev) => ({
      ...prev,
      [gradeKey]: buildExpectedExternalLabel(grade, group.code, currentPrefix),
    }));
  };

  const handleReset = () => {
    setGroupDrafts({ ...initialGroupDrafts });
    setMappingDrafts({ ...initialMappingDrafts });
    setGradeSelectionDrafts({ ...initialGradeSelectionDrafts });
    setMasterPrefixDrafts({ ...initialMasterPrefixDrafts });
    setManualOverrideByGrade({ ...initialManualOverrideByGrade });
  };

  const handleSave = async () => {
    if (!hasChanges) return;

    const changedGroupUpdatesByModule = {};
    currentGroupSelections.forEach((item) => {
      const groupKey = buildGroupKey(item.module, item.code);

      if (!changedGroupUpdatesByModule[item.module]) {
        changedGroupUpdatesByModule[item.module] = {
          selections: [],
          prefixes: [],
        };
      }

      const nextValue = groupDrafts[groupKey] || false;
      if (nextValue !== !!item.is_enabled) {
        changedGroupUpdatesByModule[item.module].selections.push({
          group_code: item.code,
          is_enabled: nextValue,
        });
      }

      const currentPrefix = normalizePrefix(masterPrefixDrafts[groupKey]);
      const initialPrefix = normalizePrefix(initialMasterPrefixDrafts[groupKey]);
      if (currentPrefix !== initialPrefix) {
        changedGroupUpdatesByModule[item.module].prefixes.push({
          group_code: item.code,
          external_label_prefix: currentPrefix || null,
        });
      }
    });

    const upserts = [];
    const deletes = [];

    editableGradeKeys.forEach((gradeKey) => {
      const currentGradeLabel = (mappingDrafts[gradeKey] || "").trim();
      const originalGradeLabel = (initialMappingDrafts[gradeKey] || "").trim();

      if (currentGradeLabel !== originalGradeLabel) {
        if (currentGradeLabel) {
          upserts.push({
            type: "grade",
            grade: gradeKey,
            external_label: currentGradeLabel,
          });
        } else if (originalGradeLabel) {
          deletes.push({
            type: "grade",
            grade: gradeKey,
          });
        }
      }
    });

    const changedGradeSelectionsByGroup = {};
    const gradeSelectionKeys = new Set([
      ...Object.keys(gradeSelectionDrafts),
      ...Object.keys(initialGradeSelectionDrafts),
    ]);

    gradeSelectionKeys.forEach((key) => {
      const [moduleKeyRaw, groupCode, grade] = key.split(KEY_SEPARATOR);
      if (!moduleKeyRaw || !groupCode || !grade) return;

      const currentValue = gradeSelectionDrafts[key];
      const initialValue = initialGradeSelectionDrafts[key];

      const resolvedCurrent = currentValue === undefined ? true : !!currentValue;
      const resolvedInitial = initialValue === undefined ? true : !!initialValue;

      if (resolvedCurrent === resolvedInitial) return;

      const groupKey = buildGroupKey(moduleKeyRaw, groupCode);
      if (!changedGradeSelectionsByGroup[groupKey]) {
        changedGradeSelectionsByGroup[groupKey] = {
          module: normalizeModule(moduleKeyRaw, null),
          group_code: groupCode,
          selections: [],
        };
      }

      changedGradeSelectionsByGroup[groupKey].selections.push({
        grade,
        is_enabled: resolvedCurrent,
      });
    });

    const hasGroupUpdates = Object.values(changedGroupUpdatesByModule).some((update) => update.selections.length > 0 || update.prefixes.length > 0);

    if (!hasGroupUpdates && upserts.length === 0 && deletes.length === 0 && Object.keys(changedGradeSelectionsByGroup).length === 0) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (hasGroupUpdates) {
        await Promise.all(
          Object.entries(changedGroupUpdatesByModule)
            .filter(([, payload]) => payload.selections.length > 0 || payload.prefixes.length > 0)
            .map(([moduleKey, payload]) =>
              dispatch(updateGlobalGroupSelections({
                moduleKey: normalizeModule(moduleKey, null),
                selections: payload.selections,
                prefixes: payload.prefixes,
              })).unwrap()
            )
        );
      }

      if (upserts.length > 0) {
        await api.post("/pay-factors/mappings", {
          mappings: upserts,
        });
      }

      if (deletes.length > 0) {
        await api.delete("/pay-factors/mappings", {
          data: {
            mappings: deletes,
          },
        });
      }

      if (Object.keys(changedGradeSelectionsByGroup).length > 0) {
        await Promise.all(
          Object.values(changedGradeSelectionsByGroup).map((payload) =>
            dispatch(updateGlobalGradeSelections({
              moduleKey: payload.module,
              groupCode: payload.group_code,
              selections: payload.selections,
            })).unwrap()
          )
        );
      }

      await Promise.all([
        dispatch(fetchGlobalPayfactorValues({ force: true })).unwrap(),
        dispatch(fetchGlobalGroupSelections()).unwrap(),
        dispatch(fetchGlobalPayfactorMappings()).unwrap(),
        dispatch(fetchGlobalGradeSelections()).unwrap(),
      ]);

      toast.success(translate("form.success.saved", "Erfolgreich gespeichert"));
    } catch (err) {
      setError(toErrorState(err, "save pay factor settings"));
      scrollInto(".content-card");
      Sentry.captureException(err);
    } finally {
      setSaving(false);
    }
  };

  const handleAdminSync = async () => {
    if (syncing) return;
    setShowSyncConfirmation(false);
    setSyncing(true);
    setSyncSummary(null);
    setError(null);

    try {
      const response = await api.post("/pay-factors/syncs");
      const syncUuid = response?.data?.data?.uuid;
      if (!syncUuid) throw new Error("The payfactor sync did not return an ID.");

      let syncRun = response.data.data;
      for (let attempt = 0; attempt < 120 && ["pending", "running"].includes(syncRun.status); attempt += 1) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        const statusResponse = await api.get(`/pay-factors/syncs/${syncUuid}`);
        syncRun = statusResponse?.data?.data || {};
      }

      if (syncRun.status !== "completed") {
        throw new Error(syncRun.error || "Payfactor synchronization did not complete.");
      }

      setSyncSummary(syncRun.summary || {});
      await Promise.all([
        dispatch(fetchPayfactors()).unwrap(),
        dispatch(fetchGlobalPayfactorValues({ force: true })).unwrap(),
        dispatch(fetchGlobalGroupSelections()).unwrap(),
        dispatch(fetchGlobalPayfactorMappings()).unwrap(),
        dispatch(fetchGlobalGradeSelections()).unwrap(),
      ]);
      toast.success(translate("payfactor_settings.sync_success", "Entgeltgruppen wurden erfolgreich aktualisiert."));
    } catch (err) {
      setError(toErrorState(err, "Entgeltgruppen aktualisieren"));
      scrollInto(".content-card");
      Sentry.captureException(err);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="col-lg col-md-9 section-col-right d-flex">
      <div className="bg-white w-100 p-4 content-card">
        <div className="d-flex justify-content-between align-items-start flex-wrap">
          <h4>{editableTranslate("global.menu.settings.pay_factors", "Entgeltgruppen zuweisen")}</h4>
          {canSyncPayfactors && (
            <Button data-testid="sync-button" variant="secondary" className="mb-2" onClick={() => setShowSyncConfirmation(true)} loading={syncing}>
              {!syncing && <i className="bi bi-cloud-download mr-2" />}
              {editableTranslate("payfactor_settings.sync_payfactors", "Entgeltgruppen aktualisieren")}
            </Button>
          )}
        </div>
        <p className="section-description">{editableTranslate("payfactor_settings.page_description", "Bearbeitung der Entgeltgruppendarstellung und der externen Bezeichnungen für den Import.")}</p>

        {syncSummary && (
          <div className="alert alert-success" role="status">
            {editableTranslate("payfactor_settings.sync_summary", "Synchronisierung abgeschlossen")}: {[
              ["created", translate("payfactor_settings.sync_created", "neu")],
              ["updated", translate("payfactor_settings.sync_updated", "aktualisiert")],
              ["retired", translate("payfactor_settings.sync_retired", "deaktiviert")],
              ["unchanged", translate("payfactor_settings.sync_unchanged", "unverändert")],
            ].map(([key, label]) => `${label}: ${syncSummary[key] || 0}`).join(" · ")}
          </div>
        )}

        {error && (
          <div className="mt-3">
            <ErrorAlert error={error} onClose={() => setError(null)} />
          </div>
        )}

        {loading && (
          <div className="mt-3 d-flex align-items-center" data-testid="loading">
            <div className="spinner-border spinner-border-sm" role="status">
              <span className="sr-only">Wird geladen...</span>
            </div>
            <span className="ml-2">{editableTranslate("form.loading", "Wird geladen...")}</span>
          </div>
        )}

        {!loading && (
          <>
            {missingSelectedGroups.length > 0 && (
              <div className="alert alert-warning mb-3" role="alert">
                {missingSelectedGroups.map((item) => (
                  <div key={buildGroupKey(item.module, item.group_code)}>
                    <strong>{editableTranslate(`global.module.${item.module_key}`, item.module_key) || item.module}</strong> / <strong>{item.group_code}</strong>: {item.message}
                  </div>
                ))}
              </div>
            )}

            {sharedGradeConflicts && (
              <div className="alert alert-info mb-3" role="alert">
                {editableTranslate("payfactor_settings.shared_grade_warning", "Einige Entgeltgruppen werden von mehreren Gruppen verwendet. Die Umbenennung einer dieser Entgeltgruppen wird global auf alle Stellen angewendet, an denen diese Entgeltgruppe vorkommt.")}
              </div>
            )}

            {hasModuleTabs && (
              <ul className="nav nav-tabs payfactor-module-tabs mb-3">
                {moduleSections.map((section) => (
                  <li className="nav-item" key={section.module}>
                    <button
                      type="button"
                      data-testid={`module-tab-${section.module_key}`}
                      className={`nav-link ${normalizeModule(activeModuleKey, null) === normalizeModule(section.module, null) ? "active" : ""}`}
                      onClick={() => setActiveModuleKey(section.module)}
                    >
                      {editableTranslate(`global.module.${section.module_key}`, section.module_key)}
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {visibleGroups.length === 0 ? (
              <p className="mb-0">{editableTranslate("payfactor_settings.no_groups", "Keine Gruppen verfügbar.")}</p>
            ) : (
              <>
                {visibleGroups.map((group) => {
                  const groupKey = group.group_key;
                  const isGroupEnabled = !!groupDrafts[groupKey];
                  const isExpanded = expandedGroups[groupKey] ?? false;
                  const isGroupDisabled = !isGroupEnabled;
                  const currentMasterPrefix = masterPrefixDrafts[groupKey] ?? group.code;

                  const enabledGradeCount = group.grades.filter((gradeRow) => {
                    const selectionKey = buildGradeSelectionKey(group.module, group.code, gradeRow.grade);
                    return gradeSelectionDrafts[selectionKey] !== false;
                  }).length;

                  return (
                    <div data-testid={`pay-factor-group-card-${groupKey}`}
                      key={groupKey}
                      className={`border rounded mb-3 overflow-hidden payfactor-group-card ${isGroupDisabled ? "payfactor-group-card-disabled" : ""}`}
                    >
                      <div className="d-flex justify-content-between align-items-center px-3 py-3 border-bottom bg-light">
                        <div data-testid="expand-toggle" onClick={() => (!isGroupEnabled || group.grades.length === 0) ? null : setExpandedGroups((prev) => ({
                          ...prev,
                          [groupKey]: !isExpanded,
                        }))} className="pr-3">
                          <div className="d-flex align-items-center flex-wrap">
                            <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'} ${!isGroupEnabled || group.grades.length === 0 ? '' : 'cursor-pointer'} mr-1`}></i>
                            <strong>{group.code}</strong>
                            <span className="badge badge-light border text-muted ml-2">
                              {group.values_count} {editableTranslate("payfactor_settings.values_count", "Werte")}
                            </span>
                            <span className="badge badge-light border text-muted ml-2">
                              {enabledGradeCount}/{group.grades.length} {editableTranslate("payfactor_settings.enabled_count_label", "aktiviert")}
                            </span>
                          </div>
                          <small className="d-block text-muted mt-1">
                            <i className={`bi bi-chevron-up cursor-pointer mr-1 invisible`}></i>
                            {editableTranslate(`global.module.${group.module_key}`, group.module_key) || group.module}
                          </small>
                        </div>

                        <div className="d-flex align-items-center">
                          <span className="toggle-switch small on-off">
                            <input id={`group-${groupKey}`} type="checkbox" checked={isGroupEnabled} onChange={(event) => handleGroupToggle(group.module, group.code, event.target.checked)} data-testid={`pay-factor-group-${groupKey}`} />
                            <label htmlFor={`group-${groupKey}`}></label>
                          </span>
                        </div>
                      </div>

                      {mappingMessagesByGroupKey[groupKey] && (
                        <div className="px-3 pt-2">
                          <small className="text-warning d-block">{mappingMessagesByGroupKey[groupKey]}</small>
                        </div>
                      )}

                      {isExpanded && (
                        <div className="p-3 bg-white">
                          {group.grades.length === 0 ? (
                            <p className="mb-0 text-muted">{editableTranslate("payfactor_settings.no_group_grades", "Es sind keine Entgeltgruppen hinterlegt.")}</p>
                          ) : (
                            <>
                              <div className="d-flex flex-wrap align-items-end justify-content-between mb-3">
                                <div className="mr-3 mb-2 payfactor-master-input-wrapper">
                                  <label className="small text-muted text-uppercase font-weight-bold mb-1 d-flex align-items-center">
                                    {editableTranslate("payfactor_settings.master_prefix", "Master-Prefix")}
                                    <i
                                      className="bi bi-info-circle ml-2 cursor-pointer"
                                      data-tooltip-id="payfactor-master-prefix-tooltip"
                                      data-tooltip-html={editableTranslateString("payfactor_settings.master_prefix_tooltip", "Definiert die Buchstabendarstellung vor der Entgeltgruppenziffer z.B. E, EG, EntG...")}
                                      aria-label={translate("payfactor_settings.master_prefix_tooltip", "Definiert die Buchstabendarstellung vor der Entgeltgruppenziffer z.B. E, EG, EntG...")}
                                    />
                                  </label>
                                  <Input data-testid="master-prefix-input"
                                    type="text"
                                    value={currentMasterPrefix}
                                    onChange={(event) => handleMasterPrefixChange(group, event.target.value)}
                                    disabled={!isGroupEnabled}
                                  />
                                </div>

                                <div className="d-flex mb-2">
                                  <Button variant="secondary" size="sm" onClick={() => handleSelectAllGrades(group, true)} disabled={!isGroupEnabled} data-testid="select-all-button" className="mr-2">
                                    {editableTranslate("button.select_all", "Alle auswählen")}
                                  </Button>
                                  <Button variant="secondary" size="sm" onClick={() => handleSelectAllGrades(group, false)} disabled={!isGroupEnabled} data-testid="deselect-all-button">
                                    {editableTranslate("button.deselect_all", "Auswahl aufheben")}
                                  </Button>
                                </div>
                              </div>

                              <div className="payfactor-grade-tiles">
                                {group.grades.map((gradeRow) => {
                                  const gradeKey = buildMappingKey(gradeRow.grade);
                                  const gradeSelectionKey = buildGradeSelectionKey(group.module, group.code, gradeRow.grade);
                                  const gradeEnabled = gradeSelectionDrafts[gradeSelectionKey] !== false;
                                  const manualOverride = !!manualOverrideByGrade[gradeKey];
                                  const sharedGroupCount = sharedGroupCountByGrade[gradeKey] || 1;
                                  const tileDisabled = !isGroupEnabled || !gradeEnabled;

                                  return (
                                    <div
                                      key={`${groupKey}${KEY_SEPARATOR}${gradeRow.grade}`}
                                      className={`border rounded p-3 payfactor-grade-tile ${tileDisabled ? "payfactor-grade-tile-disabled" : ""}`}
                                    >
                                      <div className="d-flex justify-content-between align-items-start mb-2">
                                        <div>
                                          <div className="h5 mb-1 d-flex align-items-center">
                                            {gradeRow.grade}
                                            {manualOverride && (
                                              <i
                                                className="bi bi-pen-fill text-normal ml-2 text-muted"
                                                data-tooltip-id="payfactor-custom-label-tooltip"
                                                data-tooltip-html={editableTranslateString("payfactor_settings.custom_label_badge", "Manuell angepasst")}
                                                aria-label={translate("payfactor_settings.custom_label_badge", "Manuell angepasst")}
                                              />
                                            )}
                                          </div>
                                          <div className="d-flex flex-wrap align-items-center">
                                            {sharedGroupCount > 1 && (
                                              <span className="badge badge-light border text-muted mr-2 mb-1">
                                                {editableTranslate("payfactor_settings.shared_grade_badge", "Gemeinsame Entgeltgruppe")} · {sharedGroupCount} {editableTranslate("payfactor_settings.group_count_label", "gruppen")}
                                              </span>
                                            )}
                                          </div>
                                        </div>

                                        <div className="d-inline-flex align-items-center">
                                          <span className="toggle-switch small on-off mr-2">
                                            <input id={`grade-${groupKey}-${gradeRow.grade}`} type="checkbox"
                                              data-testid={`pay-factor-grade-${groupKey}-${gradeRow.grade}`}
                                              checked={gradeEnabled}
                                              onChange={(event) => handleGradeSelectionToggle(
                                                group.module,
                                                group.code,
                                                gradeRow.grade,
                                                event.target.checked,
                                              )}
                                              disabled={!isGroupEnabled} />
                                            <label htmlFor={`grade-${groupKey}-${gradeRow.grade}`}></label>
                                          </span>
                                          <span>{editableTranslate("payfactor_settings.grade_enabled_toggle", "aktiv")}</span>
                                        </div>
                                      </div>

                                      <label className="small text-muted text-uppercase font-weight-bold mb-1 d-flex align-items-center">
                                        {editableTranslate("payfactor_settings.external_label", "Neue Darstellung")}
                                        <i
                                          className="bi bi-info-circle ml-2 cursor-pointer"
                                          data-tooltip-id="payfactor-external-label-tooltip"
                                          data-tooltip-html={editableTranslateString("payfactor_settings.external_label_tooltip", "Die Darstellung der Bezeichnung der Entgeltgruppe erfolgt auf Grundlage ihrer Vorgaben für den Import. Das Programm übernimmt diese Bezeichnung unverändert und stellt sie entsprechend dar.")}
                                          aria-label={translate("payfactor_settings.external_label_tooltip", "Die Darstellung der Bezeichnung der Entgeltgruppe erfolgt auf Grundlage ihrer Vorgaben für den Import. Das Programm übernimmt diese Bezeichnung unverändert und stellt sie entsprechend dar.")}
                                        />
                                      </label>
                                      <Input data-testid={`external-label-input-${gradeRow.grade}`}
                                        type="text"
                                        value={mappingDrafts[gradeKey] || ""}
                                        onChange={(event) => handleManualGradeMappingChange(gradeRow.grade, event.target.value)}
                                        placeholder={gradeRow.grade_label || gradeRow.grade}
                                        disabled={tileDisabled}
                                      />

                                      <div className="d-flex justify-content-end align-items-center mt-2">
                                        {manualOverride && isGroupEnabled && (
                                          <Button
                                            variant="ghost" size="inline"
                                            onClick={() => handleResetGradeToPrefix(group, gradeRow.grade)} data-testid="reset-override-button"
                                          >
                                            {editableTranslate("payfactor_settings.reset_override", "Überschreibung zurücksetzen")}
                                          </Button>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                <div className="settings-sticky-footer">
                  <hr className="mt-0" />
                  <Button variant="secondary" onClick={handleReset} loading={saving} disabled={!hasChanges} data-testid="reset-button" className="mr-2">
                    {editableTranslate("button.reset", "Abbrechen")}
                  </Button>
                  <Button variant="primary" onClick={handleSave} loading={saving} disabled={!hasChanges} data-testid="save-button">{editableTranslate("button.save", "Speichern")}</Button>
                </div>
              </>
            )}

            {shouldShowValuesViewer && (
              <div className="border rounded p-4 mt-4 bg-white">
                <div className="d-flex flex-wrap align-items-end justify-content-between mb-3">
                  <div className="mr-3 mb-2">
                    <h5 className="mb-1">{editableTranslate("payfactor_settings.values_viewer_title", "Werte der Entgelttabelle")}</h5>
                    <p className="mb-0 text-muted">
                      {editableTranslate("payfactor_settings.values_viewer_description", "Hier können die hinterlegten Werte der aktiven Entgelttabelle eingesehen werden.")}
                    </p>
                  </div>

                  <div className="mb-2" style={{ minWidth: "320px", maxWidth: "520px", width: "100%" }}>
                    <label className="small text-muted text-uppercase font-weight-bold mb-1 d-block">
                      {editableTranslate("payfactor_settings.values_viewer_selector_label", "Aktive Entgelttabelle")}
                    </label>
                    <NativeSelect data-testid="payfactor-select"
                      value={selectedViewerPayfactorHashId || ""}
                      onChange={(event) => setSelectedViewerPayfactorHashId(event.target.value || null)}
                      disabled={payfactors.length === 0}
                    >
                      {payfactors.length === 0 && (
                        <option value="">{translate("payfactor_settings.values_viewer_no_table", "Keine Entgeltfaktor-Tabelle verfügbar")}</option>
                      )}

                      {payfactors.map((payfactor) => (
                        <option key={payfactor.hash_id} value={payfactor.hash_id}>
                          {payfactor.name || "-"}
                        </option>
                      ))}
                    </NativeSelect>
                  </div>
                </div>

                {selectedViewerPayfactor && (
                  <div className="small text-muted mb-3">
                    {editableTranslate("payfactor_settings.values_viewer_selected_label", "Ausgewählt")}:
                    {" "}
                    <strong>{selectedViewerPayfactor.name || "-"}</strong>
                  </div>
                )}

                <div id="matrix-toolbar" className="payfactor-values-toolbar border rounded p-3 mb-3 bg-light sticky z-100">
                  <div className="form-row">
                    <div className="col-xl-4 col-lg-5 mb-2">
                      <label className="small text-muted text-uppercase font-weight-bold mb-1 d-block">
                        {editableTranslate("payfactor_settings.values_viewer_search_label", "Suche")}
                      </label>
                      <Input data-testid="search-input"
                        type="text"
                        value={viewerSearchTerm}
                        onChange={(event) => setViewerSearchTerm(event.target.value)}
                        placeholder={translate("payfactor_settings.values_viewer_search_placeholder", "Nach Wert, Entgeltgruppe oder Stufe suchen")}
                      />
                    </div>

                    <div className="col-xl-3 col-lg-3 mb-2">
                      <label className="small text-muted text-uppercase font-weight-bold mb-1 d-block">
                        {editableTranslate("payfactor_settings.values_viewer_group_filter_label", "Entgeltgruppe")}
                      </label>
                      <NativeSelect data-testid="group-filter-select"
                        value={viewerGroupFilter}
                        onChange={(event) => setViewerGroupFilter(event.target.value)}
                      >
                        <option value="all">{translate("payfactor_settings.values_viewer_group_filter_all", "Alle Gruppen")}</option>
                        {viewerGroupOptions.map((option) => (
                          <option key={option.key} value={option.key}>
                            {`${option.group_code || "-"} (${resolveModuleLabel(option.module_key, option.module)})`}
                          </option>
                        ))}
                      </NativeSelect>
                    </div>

                    <div className="col-xl-2 col-lg-2 mb-2">
                      <label className="small text-muted text-uppercase font-weight-bold mb-1 d-block">
                        {editableTranslate("payfactor_settings.values_viewer_level_filter_label", "Stufe")}
                      </label>
                      <NativeSelect data-testid="level-filter-select"
                        value={viewerLevelFilter}
                        onChange={(event) => setViewerLevelFilter(event.target.value)}
                      >
                        <option value="all">{translate("payfactor_settings.values_viewer_level_filter_all", "Alle")}</option>
                        <option value="with_level">{translate("payfactor_settings.values_viewer_level_filter_with", "Mit Stufe")}</option>
                        <option value="without_level">{translate("payfactor_settings.values_viewer_level_filter_without", "Ohne Stufe")}</option>
                      </NativeSelect>
                    </div>

                    <div className="col-xl-3 col-lg-2 mb-2 d-flex align-items-end justify-content-lg-end payfactor-values-toolbar-actions">
                      <Button variant="secondary" size="sm" onClick={handleExportViewerCsv} disabled={filteredViewerValues.length === 0} data-testid="export-csv-button">
                        {editableTranslate("payfactor_settings.values_viewer_export", "CSV exportieren")}
                      </Button>
                    </div>
                  </div>
                </div>

                {viewerValuesLoading && (
                  <div className="d-flex align-items-center" data-testid="loading">
                    <div className="spinner-border spinner-border-sm" role="status">
                      <span className="sr-only">Wird geladen...</span>
                    </div>
                    <span className="ml-2">{editableTranslate("form.loading", "Wird geladen...")}</span>
                  </div>
                )}

                {!viewerValuesLoading && viewerValuesError && (
                  <ErrorAlert error={viewerValuesError} onClose={() => setViewerValuesError(null)} />
                )}

                {!viewerValuesLoading && !viewerValuesError && selectedViewerPayfactorHashId && viewerValues.length === 0 && (
                  <p className="mb-0 text-muted">{editableTranslate("payfactor_settings.values_viewer_selected_table_no_values", "Die ausgewählte Entgelt-/Besoldungstabelle enthält keine Werte für dieses Modul.")}</p>
                )}

                {!viewerValuesLoading && !viewerValuesError && selectedViewerPayfactorHashId && viewerValues.length > 0 && filteredViewerValues.length === 0 && (
                  <p className="mb-0 text-muted">{editableTranslate("payfactor_settings.values_viewer_no_filtered_values", "Keine Werte entsprechen den aktuellen Filtern.")}</p>
                )}

                {!viewerValuesLoading && !viewerValuesError && filteredViewerValues.length > 0 && (
                  <div className="payfactor-values-container">
                    {viewerMatrixSections.map((section, index) => {
                      const hasBaseColumn = section.has_unlevelled_values;
                      const columnCount = 1 + section.levels.length + (hasBaseColumn ? 1 : 0);

                      return (
                        <div key={section.group_key} className="payfactor-values-matrix-section border rounded mb-3">
                          <div ref={el => (matrixHeaderRefs.current[index] = el)} className="payfactor-values-matrix-header px-3 py-2 d-flex justify-content-between align-items-center flex-wrap position-sticky z-3">
                            <div>
                              <strong>{section.group_code || "-"}</strong>
                              <small className="text-muted ml-2">
                                {resolveModuleLabel(section.module_key, section.module)}
                              </small>
                            </div>
                            <span className="badge badge-light border text-muted">
                              {formatViewerCount(section.grades.length)} {editableTranslate("payfactor_settings.values_viewer_summary_grades", "Entgeltgruppen")}
                            </span>
                          </div>

                          <PayfactorValuesMatrixTable section={section} columnCount={columnCount} hasBaseColumn={hasBaseColumn} matrixTableTheadRefs={matrixTableTheadRefs} index={index} />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </>
        )}
        <ReactTooltip id="payfactor-custom-label-tooltip" place="bottom" clickable={true} />
        <ReactTooltip id="payfactor-master-prefix-tooltip" place="bottom" clickable={true} />
        <ReactTooltip id="payfactor-external-label-tooltip" place="bottom" clickable={true} />
        <Modal
          open={showSyncConfirmation}
          title={editableTranslate("payfactor_settings.sync_confirmation_title", "Entgeltgruppen aktualisieren")}
          okLabel={editableTranslate("payfactor_settings.sync_confirm", "Jetzt aktualisieren")}
          cancelLabel={editableTranslate("button.cancel", "Abbrechen")}
          onClose={() => setShowSyncConfirmation(false)}
          onConfirm={handleAdminSync}
        >
          {editableTranslate(
            "payfactor_settings.sync_confirmation_body",
            "Die neuesten Entgeltgruppen werden abgerufen und mit den vorhandenen Daten abgeglichen. Möchten Sie fortfahren?"
          )}
        </Modal>
      </div>
    </div>
  );
}
